import React from "react";
import Swal from "sweetalert2";
import { getApiBaseUrl as sharedApiBaseUrl } from "../services/api";
import FileUploadPanelLayout from "./FileUploadPanelLayout";

const MAX_TOTAL_BYTES = 25 * 1024 * 1024; // 25MB
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

const isImageType = (item) => {
  const t = (item.type || item.file?.type || "").toLowerCase();
  const name = (item.name || "").toLowerCase();
  return t.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(name);
};

const resolveFileUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const base = sharedApiBaseUrl() || "";
  return base ? `${base.replace(/\/$/, "")}/${s.replace(/^\//, "")}` : `/${s.replace(/^\//, "")}`;
};

/**
 * Standalone invoice file upload component.
 * Allowed formats: PDF, JPG, PNG. Max total size 25MB per instance.
 */
const InvoiceUpload = ({
  title,
  value = [],
  onChange,
  helperText = "Choose invoice files to attach documents",
  className = "",
  required = false,
  invalid = false,
}) => {
  const inputRef = React.useRef(null);

  // When parent clears attachments (e.g. new invoice search), reset the file input so the same file can be chosen again.
  React.useEffect(() => {
    if (inputRef.current && (!value || value.length === 0)) {
      inputRef.current.value = "";
    }
  }, [value]);

  const totalSize = value.reduce((sum, item) => sum + (item.file?.size || item.size || 0), 0);
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
  const maxSizeMB = (MAX_TOTAL_BYTES / (1024 * 1024)).toFixed(0);

  const showMaxLimitPrompt = React.useCallback(() => {
    Swal.fire({
      icon: "warning",
      title: "Maximum Upload Limit Reached",
      text: `You can upload up to ${maxSizeMB} MB in this attachment section. Remove some files to add more.`,
      confirmButtonColor: "#ff9800",
    });
  }, [maxSizeMB]);

  const getOpenUrl = React.useCallback((fileItem) => {
    if (fileItem.file) return URL.createObjectURL(fileItem.file);
    if (isImageType(fileItem)) {
      return fileItem.preview || (fileItem.url || fileItem.path ? resolveFileUrl(fileItem.url || fileItem.path) : "");
    }
    return resolveFileUrl(fileItem.url || fileItem.path);
  }, []);

  const handleOpenInNewTab = React.useCallback((fileItem) => {
    const url = getOpenUrl(fileItem);
    if (!url) return;
    window.open(url, "_blank");
    if (fileItem.file && url.startsWith("blob:")) {
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      }, 60000);
    }
  }, [getOpenUrl]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (totalSize >= MAX_TOTAL_BYTES) {
      e.target.value = "";
      showMaxLimitPrompt();
      return;
    }

    const validFiles = files.filter((file) => ALLOWED_TYPES.includes(file.type));
    const pending = [];
    let currentTotal = totalSize;
    let skippedDueToLimit = 0;

    validFiles.forEach((file) => {
      if (currentTotal + file.size > MAX_TOTAL_BYTES) {
        skippedDueToLimit += 1;
        return;
      }
      currentTotal += file.size;
      pending.push(file);
    });

    if (pending.length === 0) {
      e.target.value = "";
      if (skippedDueToLimit > 0) {
        showMaxLimitPrompt();
      }
      return;
    }

    const newItems = [];
    let completed = 0;
    const totalToProcess = pending.length;

    const tryComplete = () => {
      completed += 1;
      if (completed === totalToProcess) {
        onChange([...value, ...newItems]);
        e.target.value = "";
        if (skippedDueToLimit > 0) {
          showMaxLimitPrompt();
        }
      }
    };

    pending.forEach((file) => {
      const fileId = Date.now() + Math.random() + newItems.length;
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newItems.push({
            id: fileId,
            file,
            name: file.name,
            preview: reader.result,
            type: file.type,
            size: file.size,
          });
          tryComplete();
        };
        reader.readAsDataURL(file);
      } else {
        newItems.push({
          id: fileId,
          file,
          name: file.name,
          preview: null,
          type: file.type,
          size: file.size,
        });
        tryComplete();
      }
    });
  };

  const handleRemove = (id) => {
    onChange(value.filter((item) => item.id !== id));
  };

  const isOverLimit = totalSize > MAX_TOTAL_BYTES;
  const canAddMore = totalSize < MAX_TOTAL_BYTES;

  const totalHint =
    value.length > 0 ? (
      <>
        Total: {totalSizeMB} MB / 25 MB
        {isOverLimit && (
          <span style={{ color: "#d9534f", marginLeft: "8px" }}>Over limit. Remove some files.</span>
        )}
      </>
    ) : null;

  return (
    <FileUploadPanelLayout
      className={className}
      title={title}
      required={required}
      invalid={invalid}
      formatNote="( Allowed formats: PDF, JPG, PNG, Max Total file size upto 25MB )"
      helperText={helperText}
      inputRef={inputRef}
      accept=".pdf,.jpg,.jpeg,.png"
      multiple
      onChange={handleFileChange}
      onChooseClick={(e) => {
        if (!canAddMore) {
          e.preventDefault();
          showMaxLimitPrompt();
        }
      }}
      chooseStyle={{
        opacity: canAddMore ? 1 : 0.6,
        backgroundColor: canAddMore ? "#fff" : "#e9ecef",
        cursor: canAddMore ? "pointer" : "not-allowed",
      }}
      totalHint={totalHint}
      files={value}
      onOpen={handleOpenInNewTab}
      onRemove={(fileItem) => handleRemove(fileItem.id)}
    />
  );
};

export default InvoiceUpload;
